import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle, Globe, Bot, Star, Search, BarChart3, MapPin, Shield, Users, Zap, TrendingUp, Award, Sparkles, Rocket, Play, Clock, Target, AlertTriangle, Calendar } from 'lucide-react';
import { testimonials, clientLogos, trustBadges, caseStudies, quickWins } from '../data/mock';
import EmailSubscription from '../components/EmailSubscription';

const Home = () => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState({});

  // Highly optimized mouse tracking with deadzone for performance
  useEffect(() => {
    let rafId = null;
    let isThrottled = false;
    let lastX = 0;
    let lastY = 0;

    const handleMouseMove = (e) => {
      if (isThrottled) return;
      
      const newX = (e.clientX / window.innerWidth) * 100;
      const newY = (e.clientY / window.innerHeight) * 100;
      
      // Only update if movement is significant (deadzone optimization)
      if (Math.abs(newX - lastX) < 1 && Math.abs(newY - lastY) < 1) return;
      
      isThrottled = true;
      rafId = requestAnimationFrame(() => {
        setMousePosition({ x: newX, y: newY });
        lastX = newX;
        lastY = newY;
        isThrottled = false;
      });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Optimized Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
            // Unobserve after animation to improve performance
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Use setTimeout to avoid blocking initial render
    setTimeout(() => {
      const elements = document.querySelectorAll('[data-animate]');
      elements.forEach((el) => observer.observe(el));
    }, 100);

    return () => observer.disconnect();
  }, []);

  const handleStartTrial = useCallback(() => {
    alert('30-Day Risk-Free Trial signup - will integrate with authentication system');
  }, []);

  const handleWatchDemo = useCallback(() => {
    alert('2-Minute Demo video - will integrate with video player or Loom');
  }, []);

  const handleScheduleConsultation = useCallback(() => {
    alert('Free consultation booking - will integrate with Calendly');
  }, []);

  // Optimized navigation function for case studies with smooth scrolling
  const handleReadCaseStudy = useCallback((caseStudy) => {
    // Map case study restaurant names to their IDs in SuccessStories page
    const caseStudyIdMap = {
      'American Pizzeria': 'american-pizzeria',
      'Madras Leaf': 'madras-leaf'
    };
    
    const caseStudyId = caseStudyIdMap[caseStudy.restaurant];
    if (caseStudyId) {
      // Navigate to success stories page and then scroll to the specific case study
      navigate('/success-stories', { state: { scrollToId: caseStudyId } });
    }
  }, [navigate]);

  const problems = [
    {
      icon: AlertTriangle,
      title: "Your Website Sends Customers Away",
      description: "Slow loading. Confusing navigation. No clear way to book. Mobile visitors bounce in 8 seconds and reserve at the competitor who shows up next in search.",
      impact: "Lost customers daily"
    },
    {
      icon: Search,
      title: "You're Invisible When Locals Search", 
      description: "Buried on page 3 while competitors own the top spots. Every day, 40+ hungry locals search for restaurants in your area and never see your name.",
      impact: "40+ missed opportunities daily"
    },
    {
      icon: Star,
      title: "Your Reviews Are Managed By Nobody",
      description: "Unanswered negative reviews from months ago. Happy customers never leave reviews because there's no system. One bad review costs you 30+ potential bookings.",
      impact: "30+ bookings lost per bad review"
    }
  ];

  const solutions = [
    {
      icon: Globe,
      title: "Premium Website Built to Convert",
      description: "Mobile-first design with fast loading speeds. Interactive menu with filters for dietary preferences. Direct integration with OpenTable, Resy, Toast, and all major booking platforms.",
      details: "Every pixel designed to answer one question: \"Should I book here?\" The answer becomes \"Yes.\"",
      impact: "3× higher conversion rate vs. generic restaurant templates",
      color: "from-brand-teal to-brand-teal-600"
    },
    {
      icon: Bot,
      title: "Isabella AI: Your 24/7 Digital Host", 
      description: "Your brand-named AI assistant that never sleeps, never calls in sick, never misses a booking opportunity.",
      details: {
        intro: "What Isabella Handles:",
        features: [
          {
            title: "Reservations",
            desc: "\"Table for 4 tomorrow at 7 PM? Done.\" Even at 2 AM when your phone goes to voicemail, Isabella is capturing bookings."
          },
          {
            title: "Instant Answers", 
            desc: "Hours, parking, dietary options, dress code, daily specials, menu details. Customers get answers in seconds—no waiting, no frustration."
          },
          {
            title: "Multiple Languages",
            desc: "English, Spanish, French, Hindi, Punjabi, Mandarin. Isabella speaks your customers' language perfectly."
          },
          {
            title: "Smart Handoffs",
            desc: "Complex requests route to your staff with full conversation context. No information lost, no repeated questions."
          }
        ],
        difference: "The Difference: While competitors send calls to voicemail after 9 PM, you're capturing 15-20 extra reservations monthly from after-hours traffic alone."
      },
      impact: "Average of 38 after-hours bookings captured in first 90 days",
      color: "from-brand-sienna to-brand-sienna-600"
    },
    {
      icon: Star,
      title: "Review Accelerator: 10× More 5-Star Reviews",
      description: "Here's why most restaurants get 1-2 reviews monthly: there's too much friction. Customers need to remember your name, search Google, find your listing, write a review. Too many steps.",
      details: {
        solution: "Our Solution: 2 hours after dining, guests receive a friendly text: \"How was your meal at [Your Restaurant]? Share your experience → [One-Tap Link]\"",
        features: [
          "10-15 new reviews monthly (instead of 1-2)",
          "Automatic AI-drafted responses to all reviews", 
          "Automatic professional replies to negative reviews",
          "Unified monitoring across Google"
        ],
        math: "The Math: Jump from 3.9★ to 4.6★ in 6 months. Every half-star increase = 19% more bookings (Harvard Business School research)."
      },
      impact: "Average restaurant goes from 1.5 to 12 reviews monthly within 60 days",
      color: "from-brand-gold to-brand-gold-600"
    },
    {
      icon: Search,
      title: "Local SEO Engine: Own the \"Near Me\" Searches",
      description: "When someone searches \"Italian restaurant near me\" or \"best brunch downtown,\" you want to show up where hungry customers are actually looking—on the first page where most clicks happen.",
      details: {
        intro: "How We Dominate Local Search:",
        features: [
          "Google Business Profile optimization (photos, posts, Q&A, categories)",
          "Local content targeting neighborhood searches",
          "Citation consistency across 50+ directories", 
          "Competitor tracking—know exactly where you rank",
          "Schema markup so Google understands your menu, hours, reviews"
        ],
        timeline: "Timeline: Quick wins in weeks 1-2. Steady improvement in rankings over 3-6 months. SEO is cumulative—results compound over time."
      },
      impact: "70% average increase in \"near me\" search visibility within 90 days", 
      color: "from-brand-forest to-brand-forest-600"
    },
    {
      icon: BarChart3,
      title: "Growth Command Center",
      description: "Stop logging into five different platforms. One dashboard shows everything that matters:",
      details: {
        features: [
          "Website traffic and source breakdown",
          "Reservations by channel (website, Isabella, phone, Google)",
          "Review trends and rating trajectory"
        ],
        extra: "Plus automated monthly reports and real-time data for fast decision-making."
      },
      impact: "All your key metrics in one place—no more juggling multiple platforms",
      color: "from-brand-teal-600 to-brand-charcoal"
    }
  ];

  return (
    <div className="pt-16 overflow-hidden">
      {/* Enhanced Hero Section with Restaurant Background */}
      <section className="relative min-h-screen sm:min-h-screen md:min-h-screen flex items-center overflow-hidden">
        {/* Restaurant Background Image - Mobile Optimized */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hero-bg-mobile"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_color-refresh-4/artifacts/wjkzkmlb_heropic.webp)'
          }}
        ></div>
        
        {/* Layered Overlays */}
        <div className="absolute inset-0">
          {/* Primary restaurant dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-restaurant-dark-900/90 via-restaurant-dark-800/85 to-restaurant-dark-700/80 hero-overlay-mobile"></div>
          
          {/* Subtle warm accent overlays with mouse parallax */}
          <div 
            className="absolute w-96 h-96 bg-gradient-to-br from-forest-primary/15 to-forest-primary-600/10 rounded-full blur-3xl transition-transform duration-300 ease-out"
            style={{
              transform: `translate3d(${-40 + mousePosition.x * 0.05}px, ${-40 + mousePosition.y * 0.05}px, 0)`,
              animation: 'float-complex 30s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-brand-gold/15 to-restaurant-dark-600/10 rounded-full blur-3xl transition-transform duration-300 ease-out"
            style={{
              transform: `translate3d(${mousePosition.x * 0.08}px, ${mousePosition.y * 0.08}px, 0)`,
              animation: 'float-complex 35s ease-in-out infinite reverse'
            }}
          ></div>

          {/* Optimized animated particles */}
          <div className="absolute inset-0">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-brand-teal/40 rounded-full animate-twinkle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 10}s`,
                  animationDuration: `${3 + Math.random() * 4}s`
                }}
              ></div>
            ))}
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-20 z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="space-y-6 sm:space-y-8">
              <div className="space-y-4 sm:space-y-6">
                <h1 
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-relaxed sm:leading-tight"
                  data-animate
                  id="main-headline"
                  style={{
                    animation: isVisible['main-headline'] ? 'fade-in-up 1s ease-out' : 'none',
                    color: '#FFFFFF'
                  }}
                >
                  <span className="block mb-2 sm:mb-1" style={{color: '#FFFFFF'}}>While You Sleep,</span>
                  <span className="block animate-pulse mb-2 sm:mb-1" style={{color: '#FFFFFF'}}>Your Competitors Are</span>
                  <span className="block" style={{color: '#FFFFFF'}}> 
                    Stealing Your Customers
                  </span>
                </h1>
                
                <p 
                  className="text-lg sm:text-xl text-brand-ivory/90 leading-relaxed max-w-2xl"
                  data-animate
                  id="hero-subheadline"
                  style={{
                    animation: isVisible['hero-subheadline'] ? 'fade-in-up 1s ease-out 0.3s both' : 'none'
                  }}
                >
                  They're capturing table reservations at 2 AM when you're closed. Answering "Do you have vegan options?" instantly. Collecting 5-star reviews automatically from every satisfied diner. 
                  Meanwhile, you're stuck with an outdated website that sends hungry customers to your competitors.
                </p>
              </div>

              {/* Social Proof Badge */}
              <div 
                className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-4 text-white group hover:bg-white/20 transition-all duration-500"
                data-animate
                id="social-proof"
                style={{
                  animation: isVisible['social-proof'] ? 'fade-in-up 0.8s ease-out 0.6s both' : 'none'
                }}
              >
                <div className="flex text-brand-gold">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" className="animate-pulse" style={{animationDelay: `${i * 0.2}s`}} />
                  ))}
                </div>
                <span className="font-medium">50+ restaurants filled more tables • Average 34% more bookings in 90 days</span>
              </div>

              {/* What PrismoSpark Delivers */}
              <div 
                className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 space-y-4"
                data-animate
                id="value-prop"
                style={{
                  animation: isVisible['value-prop'] ? 'fade-in-up 0.8s ease-out 0.9s both' : 'none'
                }}
              >
                <h3 className="text-lg font-semibold text-white">What PrismoSpark Delivers:</h3>
                <p className="text-brand-ivory/90">
                  <span className="text-brand-teal font-medium">Premium website</span> + <span className="text-brand-sienna font-medium">Isabella AI assistant</span> + <span className="text-brand-gold font-medium">automated review growth</span> + <span className="text-brand-forest font-medium">local SEO dominance</span> + <span className="text-brand-teal-600 font-medium">unified growth dashboard</span>. Everything you need to compete—and win.
                </p>
              </div>

              {/* Modern CTA Buttons */}
              <div 
                className="flex flex-col sm:flex-row gap-4 sm:gap-6 pt-6"
                data-animate
                id="cta-buttons"
                style={{
                  animation: isVisible['cta-buttons'] ? 'fade-in-up 0.8s ease-out 1.2s both' : 'none'
                }}
              >
                <Button 
                  onClick={handleStartTrial}
                  className="group relative font-bold text-base sm:text-lg px-6 sm:px-12 py-4 sm:py-5 rounded-2xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden w-full sm:w-auto"
                  style={{
                    backgroundColor: '#D4623C',
                    color: '#FFFFFF',
                    boxShadow: '0 25px 50px -12px rgba(212, 98, 60, 0.25)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#B8532F';
                    e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#D4623C';
                    e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.25)';
                  }}
                >
                  {/* Animated background layer */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
                  
                  {/* Content */}
                  <div className="relative z-10 flex items-center justify-center space-x-3">
                    <Zap className="w-6 h-6 group-hover:animate-pulse" />
                    <span>Start 30-Day Risk-Free Trial</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </Button>
                
                <Button 
                  onClick={handleWatchDemo}
                  className="group relative font-semibold text-base sm:text-lg px-6 sm:px-10 py-4 sm:py-5 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] overflow-hidden w-full sm:w-auto"
                  style={{
                    backgroundColor: 'transparent',
                    border: '2px solid #0A7A6E',
                    color: '#FFFFFF'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#085A54';
                    e.target.style.backgroundColor = 'rgba(10, 122, 110, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#0A7A6E';
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  {/* Content */}
                  <div className="relative z-10 flex items-center justify-center space-x-3">
                    <Play className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                    <span>Watch Demo</span>
                  </div>
                </Button>
              </div>

              {/* Trust Strip */}
              <div 
                className="flex flex-wrap gap-6 text-sm text-slate-400"
                data-animate
                id="trust-strip"
                style={{
                  animation: isVisible['trust-strip'] ? 'fade-in-up 0.6s ease-out 1.5s both' : 'none'
                }}
              >
                {[
                  "✅ No credit card required",
                  "✅ Full setup included", 
                  "✅ Cancel anytime",
                  "✅ 30-day money-back guarantee"
                ].map((item, index) => (
                  <span key={index} className="hover:text-white transition-colors duration-300">{item}</span>
                ))}
              </div>
            </div>

            {/* Enhanced Hero Visual Elements */}
            <div className="relative">
              <div 
                className="relative group"
                style={{
                  transform: `perspective(1000px) rotateY(${mousePosition.x * 0.02 - 1}deg) rotateX(${mousePosition.y * 0.02 - 1}deg)`
                }}
              >
                {/* Isabella AI Interface Card */}
                <div className="relative animate-float-3d mb-8">
                  <div className="bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl transform rotate-3 group-hover:rotate-1 transition-all duration-700">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-8 h-8 bg-gradient-to-br from-brand-teal to-brand-teal-600 rounded-full flex items-center justify-center">
                        <Bot size={16} className="text-white" />
                      </div>
                      <div>
                        <div className="text-brand-charcoal font-semibold">Isabella AI</div>
                        <div className="text-xs text-brand-forest flex items-center">
                          <div className="w-2 h-2 bg-brand-forest rounded-full mr-1 animate-pulse"></div>
                          Online 24/7
                        </div>
                      </div>
                      <Clock className="text-brand-charcoal/60 ml-auto" size={16} />
                    </div>
                    
                    <div className="space-y-3 text-sm">
                      <div className="bg-sienna-accent text-white p-3 rounded-lg rounded-br-none">
                        "Hi! Table for 4 tomorrow at 7 PM?"
                      </div>
                      <div className="bg-white/95 text-restaurant-dark-800 p-3 rounded-lg rounded-bl-none border border-restaurant-dark/20">
                        Perfect! I can reserve that for you. What name should I put it under?
                      </div>
                      <div className="bg-sienna-accent text-white p-3 rounded-lg rounded-br-none">
                        "Rajesh Kumar"
                      </div>
                      <div className="bg-white/95 text-restaurant-dark-800 p-3 rounded-lg rounded-bl-none border border-restaurant-dark/20">
                        Excellent! Your table for 4 is confirmed for tomorrow at 7 PM. ✅
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating success metrics */}
                <div className="absolute -top-6 -right-6 animate-float-delayed-3d">
                  <div className="bg-gradient-to-br from-forest-primary/95 to-forest-primary-600/95 backdrop-blur-md border border-white/20 rounded-xl p-4 transform -rotate-12 shadow-xl">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">+340%</div>
                      <div className="text-xs text-white/80">Bookings</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-6 animate-float-3d" style={{animationDelay: '1s'}}>
                  <div className="bg-gradient-to-br from-brand-gold/90 to-brand-gold-600/90 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-xl">
                    <div className="flex items-center space-x-2">
                      <Star className="text-white" size={16} fill="currentColor" />
                      <span className="text-white text-sm font-bold">4.8★ Rating</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Restaurant Industry Reality Section */}
      <section className="py-20 bg-gradient-to-br from-restaurant-dark-900 to-restaurant-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="inline-block mb-6 bg-forest-primary/20 text-forest-primary border-forest-primary/30 px-6 py-3">
              The Restaurant Reality
            </Badge>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8">
              While You're Running Service Tonight...
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-16">
            {[
              {
                stat: "73%",
                label: "of diners research restaurants online before visiting",
                icon: "🔍",
                detail: "Your online presence IS your first impression"
              },
              {
                stat: "2 AM",
                label: "peak time for weekend reservation searches",
                icon: "🌙",
                detail: "Customers book while you sleep"
              },
              {
                stat: "8 seconds",
                label: "average time before mobile visitors bounce",
                icon: "⚡",
                detail: "Slow sites = lost customers"
              },
              {
                stat: "1 bad review",
                label: "can cost you 30+ potential bookings",
                icon: "⭐",
                detail: "Reviews directly impact your bottom line"
              }
            ].map((item, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl mb-4">{item.icon}</div>
                  <div className="text-3xl font-bold text-forest-primary mb-2">{item.stat}</div>
                  <div className="text-sm font-medium mb-3">{item.label}</div>
                  <div className="text-xs text-brand-ivory/80">{item.detail}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-gradient-to-r from-brand-red/20 to-restaurant-dark-600/30 backdrop-blur-md border border-brand-red/30 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Every Day You Wait, You're Losing Revenue</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 text-white">
              <div>
                <div className="text-xl font-bold text-brand-red">Empty Tables</div>
                <div className="text-sm">Customers can't find you online</div>
              </div>
              <div>
                <div className="text-xl font-bold text-brand-red">Lost Bookings</div>
                <div className="text-sm">Competitors capture your customers</div>
              </div>
              <div>
                <div className="text-xl font-bold text-brand-red">Poor Reviews</div>
                <div className="text-sm">Reputation damage compounds daily</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-32 bg-gradient-to-br from-brand-ivory to-brand-ivory-100 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20" data-animate id="problems-header">
            <Badge className="inline-block mb-8 bg-brand-red/20 text-brand-red border-brand-red/30 px-6 py-3">
              Revenue Leaks Identified
            </Badge>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-brand-charcoal mb-8"
              style={{
                animation: isVisible['problems-header'] ? 'fade-in-up 0.8s ease-out' : 'none'
              }}
            >
              Three Leaks Draining Your Revenue—
              <span className="block text-brand-red">We Plug Them All</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {problems.map((problem, index) => (
              <Card 
                key={index} 
                className="group bg-white border-2 border-brand-red/20 hover:border-brand-red/40 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4"
                data-animate
                id={`problem-${index}`}
                style={{
                  animation: isVisible[`problem-${index}`] ? `fade-in-up 0.8s ease-out ${index * 200}ms both` : 'none'
                }}
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-brand-red/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-brand-red/30 transition-colors duration-300">
                    <problem.icon className="text-brand-red" size={32} />
                  </div>
                  <CardTitle className="text-xl text-brand-charcoal group-hover:text-brand-red transition-colors duration-300">
                    {problem.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-brand-charcoal-600 leading-relaxed mb-4 text-base">
                    {problem.description}
                  </CardDescription>
                  <div className="bg-brand-red/10 border border-brand-red/30 rounded-lg p-4">
                    <div className="text-brand-red font-semibold text-sm">
                      💸 Cost: {problem.impact}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-32 bg-gradient-to-br from-restaurant-dark-900 via-restaurant-dark-800 to-restaurant-dark-700 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-forest-primary/10 to-forest-primary-600/8 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-sienna-accent/10 to-restaurant-dark-600/8 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20" data-animate id="solutions-header">
            <Badge className="inline-block mb-8 bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 py-3">
              Complete Solution
            </Badge>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-8"
              style={{
                animation: isVisible['solutions-header'] ? 'fade-in-up 0.8s ease-out' : 'none'
              }}
            >
              Everything Your Restaurant Needs to 
              <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent">
                Dominate Local Search
              </span>
            </h2>
          </div>

          <div className="space-y-20">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
                data-animate
                id={`solution-${index}`}
                style={{
                  animation: isVisible[`solution-${index}`] ? `fade-in-${index % 2 === 0 ? 'left' : 'right'} 0.8s ease-out` : 'none'
                }}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <Card className="bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 transform hover:-translate-y-2">
                    <CardHeader>
                      <div className={`w-20 h-20 bg-gradient-to-br ${solution.color} rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 hover:rotate-12 transition-all duration-500 shadow-lg`}>
                        <solution.icon className="text-white" size={36} />
                      </div>
                      <CardTitle className="text-2xl text-white mb-4">{solution.title}</CardTitle>
                      <CardDescription className="text-slate-300 leading-relaxed text-lg mb-6">
                        {solution.description}
                      </CardDescription>

                      {/* Enhanced Details Section */}
                      <div className="space-y-6">
                        {/* Simple details string */}
                        {typeof solution.details === 'string' && (
                          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                            <p className="text-slate-200 italic">{solution.details}</p>
                          </div>
                        )}

                        {/* Complex details object */}
                        {typeof solution.details === 'object' && solution.details !== null && (
                          <div className="space-y-4">
                            {solution.details.intro && (
                              <h4 className="text-lg font-semibold text-white">{solution.details.intro}</h4>
                            )}

                            {solution.details.solution && (
                              <div className="bg-gradient-to-r from-white/5 to-white/10 rounded-lg p-4 border border-white/10">
                                <p className="text-slate-200 font-medium">{solution.details.solution}</p>
                              </div>
                            )}

                            {solution.details.features && (
                              <div className="space-y-3">
                                {Array.isArray(solution.details.features) && solution.details.features.map((feature, featureIndex) => (
                                  <div key={featureIndex}>
                                    {typeof feature === 'string' ? (
                                      <div className="flex items-start space-x-3">
                                        <div className={`w-2 h-2 bg-gradient-to-r ${solution.color} rounded-full mt-2 flex-shrink-0`}></div>
                                        <span className="text-slate-300">{feature}</span>
                                      </div>
                                    ) : (
                                      <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-2">
                                        <h5 className="text-white font-semibold">{feature.title}</h5>
                                        <p className="text-slate-300 text-sm leading-relaxed">{feature.desc}</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {solution.details.difference && (
                              <div className="bg-gradient-to-r from-brand-forest/20 to-brand-forest-600/20 border border-brand-forest/20 rounded-lg p-4">
                                <p className="text-brand-forest-100 font-medium">{solution.details.difference}</p>
                              </div>
                            )}

                            {solution.details.math && (
                              <div className="bg-gradient-to-r from-brand-gold/20 to-brand-gold-600/20 border border-brand-gold/20 rounded-lg p-4">
                                <p className="text-brand-gold-100 font-medium">{solution.details.math}</p>
                              </div>
                            )}

                            {solution.details.timeline && (
                              <div className="bg-gradient-to-r from-brand-teal/20 to-brand-teal-600/20 border border-brand-teal/20 rounded-lg p-4">
                                <p className="text-brand-teal-100 font-medium">{solution.details.timeline}</p>
                              </div>
                            )}

                            {solution.details.extra && (
                              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                <p className="text-slate-200">{solution.details.extra}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="bg-gradient-to-r from-white/10 to-white/5 rounded-lg p-4 border border-white/10 mt-6">
                        <div className="text-white font-semibold flex items-center space-x-2">
                          <TrendingUp size={20} className="text-brand-forest" />
                          <span>Impact: {solution.impact}</span>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </div>

                <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''} relative`}>
                  <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                    {/* Feature visualization based on solution type */}
                    {solution.title.includes('Website') && (
                      <div className="space-y-4">
                        <div className="h-40 bg-gradient-to-br from-brand-teal/20 to-brand-teal-600/20 rounded-lg flex items-center justify-center">
                          <Globe size={48} className="text-brand-teal" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="h-12 bg-white/10 rounded flex items-center justify-center text-white text-xs">Mobile First</div>
                          <div className="h-12 bg-white/10 rounded flex items-center justify-center text-white text-xs">Fast Loading</div>
                          <div className="h-12 bg-white/10 rounded flex items-center justify-center text-white text-xs">Easy Booking</div>
                          <div className="h-12 bg-white/10 rounded flex items-center justify-center text-white text-xs">Menu Filters</div>
                        </div>
                      </div>
                    )}
                    
                    {solution.title.includes('Isabella') && (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-brand-sienna to-brand-sienna-600 rounded-full flex items-center justify-center">
                            <Bot size={20} className="text-white" />
                          </div>
                          <div>
                            <div className="text-white font-semibold">Isabella AI</div>
                            <div className="text-xs text-brand-forest">Available 24/7</div>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="bg-brand-teal/80 text-white p-2 rounded">Reservations ✓</div>
                          <div className="bg-brand-sienna/80 text-white p-2 rounded">6 Languages ✓</div>
                          <div className="bg-brand-forest/80 text-white p-2 rounded">Smart Handoffs ✓</div>
                          <div className="bg-brand-gold/80 text-white p-2 rounded">38 After-Hours Bookings/Month ✓</div>
                        </div>
                      </div>
                    )}

                    {solution.title.includes('Review') && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-5 gap-2 mb-4">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={24} className="text-brand-gold" fill="currentColor" />
                          ))}
                        </div>
                        <div className="space-y-2">
                          <div className="bg-white/10 p-3 rounded text-white text-sm">
                            "Amazing food and service! 5 stars!" ⭐⭐⭐⭐⭐
                          </div>
                          <div className="bg-brand-teal/20 p-2 rounded text-white text-xs">
                            AI Response: Thank you so much for the wonderful review!
                          </div>
                        </div>
                        <div className="text-center text-brand-forest font-semibold">
                          10-15 reviews/month ↗️
                        </div>
                      </div>
                    )}

                    {solution.title.includes('SEO') && (
                      <div className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-white">
                            <span className="text-sm">"restaurants near me"</span>
                            <Badge className="bg-brand-forest text-white">#1</Badge>
                          </div>
                          <div className="flex justify-between items-center text-white">
                            <span className="text-sm">"best food downtown"</span>
                            <Badge className="bg-brand-forest text-white">#2</Badge>
                          </div>
                          <div className="flex justify-between items-center text-white">
                            <span className="text-sm">"pizza delivery"</span>
                            <Badge className="bg-brand-gold text-white">#3</Badge>
                          </div>
                        </div>
                        <div className="text-center text-brand-forest font-semibold">
                          +70% search visibility
                        </div>
                      </div>
                    )}

                    {solution.title.includes('Command Center') && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white/10 p-3 rounded text-center">
                            <div className="text-2xl font-bold text-brand-teal">340%</div>
                            <div className="text-xs text-white">Traffic</div>
                          </div>
                          <div className="bg-white/10 p-3 rounded text-center">
                            <div className="text-2xl font-bold text-brand-forest">47</div>
                            <div className="text-xs text-white">Bookings/wk</div>
                          </div>
                          <div className="bg-white/10 p-3 rounded text-center">
                            <div className="text-2xl font-bold text-brand-gold">4.8★</div>
                            <div className="text-xs text-white">Rating</div>
                          </div>
                          <div className="bg-white/10 p-3 rounded text-center">
                            <div className="text-2xl font-bold text-brand-sienna">89</div>
                            <div className="text-xs text-white">Reviews</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Restaurant Owner Reality Check */}
      <section className="py-20 bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-brand-ivory-50 to-brand-sienna/10 border-2 border-brand-sienna/30 overflow-hidden">
            <CardContent className="p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">👨‍🍳</div>
                <h3 className="text-2xl font-bold text-brand-charcoal mb-4">
                  "I Was Tired of Working 14-Hour Days Just to See Empty Tables"
                </h3>
                <div className="text-brand-charcoal-600 mb-6">— Restaurant Owner, Toronto</div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 text-center">
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-brand-red/20 rounded-full flex items-center justify-center mx-auto">
                    <AlertTriangle className="text-brand-red" size={24} />
                  </div>
                  <h4 className="font-semibold text-brand-charcoal">Before PrismoSpark</h4>
                  <div className="space-y-2 text-sm text-brand-charcoal-600">
                    <div>• Empty tables during peak hours</div>
                    <div>• Phone calls going to voicemail</div>
                    <div>• Negative reviews left unanswered</div>
                    <div>• Competitors stealing customers</div>
                    <div>• Stress about paying rent each month</div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto">
                    <ArrowRight className="text-brand-teal" size={24} />
                  </div>
                  <h4 className="font-semibold text-brand-charcoal">90-Day Transformation</h4>
                  <div className="space-y-2 text-sm text-brand-charcoal-600">
                    <div>• Professional website launched</div>
                    <div>• Isabella AI handling bookings 24/7</div>
                    <div>• Automatic review management</div>
                    <div>• Local SEO ranking improvements</div>
                    <div>• Complete online presence overhaul</div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-brand-forest/20 rounded-full flex items-center justify-center mx-auto">
                    <TrendingUp className="text-brand-forest" size={24} />
                  </div>
                  <h4 className="font-semibold text-brand-charcoal">Now</h4>
                  <div className="space-y-2 text-sm text-brand-charcoal-600">
                    <div className="font-semibold text-brand-forest">• 40% more bookings monthly</div>
                    <div className="font-semibold text-brand-forest">• 4.8-star average rating</div>
                    <div className="font-semibold text-brand-forest">• Consistent weekend waitlists</div>
                    <div className="font-semibold text-brand-forest">• Peace of mind about the future</div>
                    <div className="font-semibold text-brand-forest">• Actually profitable business</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-brand-charcoal rounded-xl p-8 mt-12 text-center">
                <div className="text-white text-lg italic mb-4">
                  "I went from worrying about making rent to having a 2-week waitlist. PrismoSpark didn't just build me a website—they built me a sustainable business."
                </div>
                <div className="text-brand-ivory/70 text-sm">
                  — Maria Giuseppe, Giuseppe's Italian Kitchen
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      {/* Case Studies Section */}
      <section className="py-32 bg-gradient-to-br from-white via-brand-ivory to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20" data-animate id="case-studies-header">
            <Badge className="inline-block mb-8 bg-gradient-to-r from-brand-teal to-brand-teal-600 text-white border-0 px-6 py-3">
              Real Results
            </Badge>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-brand-charcoal mb-8"
              style={{
                animation: isVisible['case-studies-header'] ? 'fade-in-up 0.8s ease-out' : 'none'
              }}
            >
              Real Restaurants. Real Growth. 
              <span className="block bg-gradient-to-r from-brand-teal to-brand-teal-600 bg-clip-text text-transparent">
                Real Fast.
              </span>
            </h2>
          </div>

          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <Card 
                key={study.id} 
                className="overflow-hidden shadow-2xl border-2 border-brand-charcoal/20 hover:shadow-3xl transition-all duration-700 transform hover:-translate-y-2"
                data-animate
                id={`case-study-${index}`}
                style={{
                  animation: isVisible[`case-study-${index}`] ? `fade-in-up 0.8s ease-out ${index * 300}ms both` : 'none'
                }}
              >
                <div className="grid lg:grid-cols-2">
                  {/* Story Side */}
                  <div className="p-12">
                    <div className="space-y-6">
                      <div className="flex items-center space-x-3 mb-6">
                        <MapPin className="text-brand-teal" size={20} />
                        <span className="text-brand-charcoal-600">{study.location}</span>
                      </div>
                      
                      <div>
                        <h3 className="text-3xl font-bold text-brand-charcoal mb-2">{study.restaurant}</h3>
                        <Badge variant="outline" className="mb-4 border-brand-charcoal/30 text-brand-charcoal">{study.cuisine}</Badge>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-brand-charcoal mb-3">The Challenge</h4>
                        <p className="text-brand-charcoal-600 leading-relaxed">{study.challenge}</p>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-brand-charcoal mb-3">The Solution</h4>
                        <p className="text-brand-charcoal-600 leading-relaxed">{study.solution}</p>
                      </div>

                      <div className="bg-brand-ivory-50 border-l-4 border-brand-teal p-6 rounded-r-lg">
                        <p className="text-brand-charcoal italic text-lg leading-relaxed mb-4">"{study.testimonial}"</p>
                        <div className="text-brand-charcoal-600 font-medium">— {study.owner}, Owner</div>
                      </div>
                    </div>
                  </div>

                  {/* Results Side */}
                  <div className="bg-gradient-to-br from-brand-teal to-brand-teal-700 p-12 text-white">
                    <h4 className="text-2xl font-bold mb-8">Results in {study.results.timeline}</h4>
                    
                    <div className="grid grid-cols-2 gap-8 mb-8">
                      {Object.entries(study.results).filter(([key]) => key !== 'timeline').map(([key, value], resultIndex) => (
                        <div key={key} className="text-center">
                          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                            {key.includes('reservations') && <Calendar className="text-white" size={24} />}
                            {key.includes('revenue') && <TrendingUp className="text-white" size={24} />}
                            {key.includes('review') && <Star className="text-white" size={24} />}
                            {key.includes('seo') && <Search className="text-white" size={24} />}
                            {key.includes('bookings') && <Clock className="text-white" size={24} />}
                            {key.includes('traffic') && <Users className="text-white" size={24} />}
                            {key.includes('rating') && <Award className="text-white" size={24} />}
                          </div>
                          <div className="text-2xl font-bold mb-2">{value}</div>
                          <div className="text-sm text-white/80 capitalize">{key.replace(/_/g, ' ')}</div>
                        </div>
                      ))}
                    </div>

                    <Button 
                      variant="outline" 
                      onClick={() => handleReadCaseStudy(study)}
                      className="w-full border-white/30 bg-white/10 text-white hover:bg-white hover:text-brand-teal transition-all duration-300"
                    >
                      Read Full Case Study
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Quick Wins */}
          <div className="mt-20" data-animate id="quick-wins">
            <h3 
              className="text-2xl font-bold text-center text-brand-charcoal mb-12"
              style={{
                animation: isVisible['quick-wins'] ? 'fade-in-up 0.6s ease-out' : 'none'
              }}
            >
              Quick Wins From Other Restaurants:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {quickWins.map((win, index) => (
                <Card 
                  key={index} 
                  className="bg-brand-ivory-50 border-brand-charcoal/20 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                  style={{
                    animation: isVisible['quick-wins'] ? `fade-in-up 0.6s ease-out ${index * 150}ms both` : 'none'
                  }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-3 h-3 bg-brand-forest rounded-full mt-2 flex-shrink-0"></div>
                      <div>
                        <h4 className="font-semibold text-brand-charcoal mb-1">{win.restaurant} — {win.location}</h4>
                        <p className="text-brand-charcoal-600">{win.result}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-gradient-to-br from-restaurant-dark-900 via-restaurant-dark-800 to-restaurant-dark-700 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-forest-primary/5 to-transparent"></div>
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-forest-primary/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-forest-primary/8 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <Badge className="inline-block mb-8 bg-forest-primary/20 border-forest-primary/40 text-forest-primary px-6 py-3">
            Risk-Free Guarantee
          </Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-8 leading-tight">
            Start Growing Today—
            <span className="block">Risk-Free</span>
          </h2>
          <div className="bg-white/5 backdrop-blur-md border border-forest-primary/20 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-white mb-4">30-Day Money-Back Guarantee</h3>
            <p className="text-xl text-white/90 leading-relaxed">
              Try PrismoSpark for 30 days. If you don't see measurable results, we'll refund every penny. No questions asked.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 justify-center mb-8">
            <Button 
              onClick={handleStartTrial}
              className="group relative font-bold text-base sm:text-lg lg:text-xl px-6 sm:px-10 lg:px-12 py-4 sm:py-5 lg:py-6 rounded-2xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden w-full sm:w-auto"
              style={{
                backgroundColor: '#D4623C',
                color: '#FFFFFF',
                boxShadow: '0 25px 50px -12px rgba(212, 98, 60, 0.25)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#B8532F';
                e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#D4623C';
                e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.25)';
              }}
            >
              {/* Content */}
              <div className="relative z-10 flex items-center justify-center space-x-3">
                <Rocket className="w-6 h-6 group-hover:animate-bounce" />
                <span>Start 30-Day Risk-Free Trial</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
              
              {/* Shine effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
            </Button>
            
            <Button 
              onClick={handleScheduleConsultation}
              className="group relative font-semibold text-base sm:text-lg lg:text-xl px-6 sm:px-8 lg:px-10 py-4 sm:py-5 lg:py-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] overflow-hidden w-full sm:w-auto"
              style={{
                backgroundColor: 'transparent',
                border: '2px solid #0A7A6E',
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#085A54';
                e.target.style.backgroundColor = 'rgba(10, 122, 110, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#0A7A6E';
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              {/* Glass background */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 via-brand-teal/5 to-transparent group-hover:from-brand-teal/20 group-hover:via-brand-teal/10 transition-all duration-300"></div>
              
              {/* Content */}
              <div className="relative z-10 flex items-center justify-center space-x-3">
                <Users className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                <span>Schedule Free Consultation</span>
              </div>
              
              {/* Border shimmer */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-brand-teal/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </Button>
          </div>
          <p className="text-brand-ivory/80 text-lg">
            Join 50+ restaurants already dominating their local markets
          </p>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-br from-brand-ivory via-brand-ivory-50 to-brand-ivory">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-brand-charcoal mb-4">
              Stay Updated with Restaurant Growth Tips
            </h2>
            <p className="text-xl text-brand-charcoal-600 max-w-2xl mx-auto">
              Get weekly insights, case studies, and exclusive tips delivered to your inbox. 
              No spam, just valuable content to help your restaurant grow.
            </p>
          </div>
          
          <div className="max-w-md mx-auto">
            <EmailSubscription 
              placeholder="Enter your email address"
              buttonText="Subscribe"
              source="homepage_newsletter"
              className="mb-4"
            />
            <p className="text-sm text-brand-charcoal-500">
              Join 500+ restaurant owners getting growth tips weekly
            </p>
          </div>
        </div>
      </section>

      {/* Enhanced CSS Animations */}
      <style jsx>{`
        @keyframes float-complex {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(30px, -30px) rotate(90deg); }
          50% { transform: translate(-20px, 20px) rotate(180deg); }
          75% { transform: translate(-30px, -10px) rotate(270deg); }
        }
        
        @keyframes float-3d {
          0%, 100% { 
            transform: translateY(0) rotateX(0deg) rotateY(6deg);
          }
          50% { 
            transform: translateY(-20px) rotateX(5deg) rotateY(6deg);
          }
        }
        
        @keyframes float-delayed-3d {
          0%, 100% { 
            transform: translateY(0) rotateX(0deg) rotateY(-12deg);
          }
          50% { 
            transform: translateY(-15px) rotateX(-5deg) rotateY(-12deg);
          }
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in-left {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fade-in-right {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .animate-float-3d { animation: float-3d 6s ease-in-out infinite; }
        .animate-float-delayed-3d { animation: float-delayed-3d 6s ease-in-out infinite 2s; }
        .animate-twinkle { animation: twinkle 3s ease-in-out infinite; }
        .animate-gradient { 
          background-size: 300% 300%;
          animation: gradient-shift 8s ease infinite; 
        }
        
        .bg-300% { background-size: 300% 300%; }
      `}</style>
    </div>
  );
};

export default Home;